/**
 * Trayectorias de deterioro clínico por caso.
 *
 * Cada paso representa un cambio en el estado del paciente que ocurre si el
 * alumno NO actúa a tiempo. Un paso puede ser prevenido ordenando ciertos
 * exámenes/tratamientos antes del tiempo de disparo.
 *
 * El tiempo se mide en minutos del paciente (state.simulation.patientTime / 60).
 * Con timeScale=60 (default), 1 minuto del paciente = 1 segundo real, así que
 * una trayectoria de 10 min se juega en ~10 segundos si no hay intervención.
 * El alumno puede bajar timeScale desde el HUD para ganar tiempo.
 */

export interface DeteriorationStep {
  id: string;
  atMinutes: number;
  nodeId: string;
  newValue: number | string;
  narrative: string;
  severity?: 'moderate' | 'severe' | 'life-threatening';
  /** Si cualquiera de estos exam IDs fue ordenado antes del disparo, el paso NO ocurre. */
  preventedByExamIds?: string[];
  /** Mensaje alternativo cuando el paso es prevenido (premia al alumno). */
  preventedNarrative?: string;
}

export interface DeteriorationTrajectory {
  caseId: string;
  steps: DeteriorationStep[];
}

export const DETERIORATION_TRAJECTORIES: Record<string, DeteriorationTrajectory> = {
  sepsis: {
    caseId: 'sepsis',
    steps: [
      {
        id: 'sepsis-lactate-up',
        atMinutes: 2,
        nodeId: 'vital-2',
        newValue: 135,
        narrative: 'Taquicardia compensatoria en progreso (FC 135). La hipoperfusión empeora.',
        severity: 'severe',
        preventedByExamIds: ['exam-lactate'],
        preventedNarrative: 'Lactato medido a tiempo: el equipo reconoció la hipoperfusión.',
      },
      {
        id: 'sepsis-pa-crash',
        atMinutes: 4,
        nodeId: 'vital-1',
        newValue: 68,
        narrative: 'PA sistólica cae a 68 mmHg. Shock séptico refractario a volumen incipiente.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-fluids'],
        preventedNarrative: 'Los cristaloides iniciados a tiempo estabilizaron la presión.',
      },
      {
        id: 'sepsis-confusion',
        atMinutes: 6,
        nodeId: 'sintoma-2',
        newValue: 'GCS 9 (O2 V3 M4). Obnubilación profunda, respuesta sólo al dolor.',
        narrative: 'Deterioro neurológico: GCS cae a 9. Considerar vía aérea avanzada.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-antibiotics'],
        preventedNarrative: 'Antibiótico administrado dentro de la hora dorada: la sepsis se contiene.',
      },
      {
        id: 'sepsis-refractory',
        atMinutes: 9,
        nodeId: 'vital-1',
        newValue: 55,
        narrative: '⚠️ Shock refractario: PA 55 mmHg pese a cristaloides. Riesgo vital inminente.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-vasopressors', 'tx-antibiotics'],
        preventedNarrative: 'Noradrenalina + antibiótico: PAM recuperada, hemodinamia estable.',
      },
    ],
  },

  epoc: {
    caseId: 'epoc',
    steps: [
      {
        id: 'epoc-fr-up',
        atMinutes: 2,
        nodeId: 'vital-1',
        newValue: 34,
        narrative: 'FR sube a 34, uso marcado de musculatura accesoria. Fatiga respiratoria incipiente.',
        severity: 'severe',
        preventedByExamIds: ['tx-bronchodilators'],
        preventedNarrative: 'Broncodilatadores administrados: la taquipnea se contiene.',
      },
      {
        id: 'epoc-spo2-crash',
        atMinutes: 4,
        nodeId: 'vital-2',
        newValue: 80,
        narrative: 'SpO2 cae a 80% pese al aire ambiente. Hipoxemia crítica.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-oxygen'],
        preventedNarrative: 'Oxigenoterapia controlada evitó la desaturación profunda.',
      },
      {
        id: 'epoc-acidosis',
        atMinutes: 6,
        nodeId: 'vital-1',
        newValue: 38,
        narrative: 'FR 38 con respiración paradojal. Acidosis respiratoria severa en progreso.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-noninvasive', 'tx-bronchodilators'],
        preventedNarrative: 'VMNI + broncodilatadores: la mecánica ventilatoria mejora.',
      },
      {
        id: 'epoc-failure',
        atMinutes: 9,
        nodeId: 'sintoma-1',
        newValue: 'Somnolencia creciente, asterixis presente, GCS 11. Falla ventilatoria inminente.',
        narrative: '⚠️ Narcosis por CO2. Requiere VMNI urgente o intubación orotraqueal.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-noninvasive'],
        preventedNarrative: 'VMNI iniciada: el CO2 baja, el paciente recupera alerta progresivamente.',
      },
    ],
  },

  iam: {
    caseId: 'iam',
    steps: [
      {
        id: 'iam-fc-up',
        atMinutes: 2,
        nodeId: 'vital-fc',
        newValue: 128,
        narrative: 'El paciente refiere aumento del dolor. FC sube a 128 lpm. La isquemia progresa.',
        severity: 'severe',
        preventedByExamIds: ['exam-ecg'],
        preventedNarrative: 'El ECG temprano permitió iniciar manejo: la FC se mantiene controlada.',
      },
      {
        id: 'iam-pa-down',
        atMinutes: 4,
        nodeId: 'vital-pa',
        newValue: 72,
        narrative: 'PA sistólica cae a 72 mmHg. Shock cardiogénico incipiente — Killip III/IV.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-aspirin'],
        preventedNarrative: 'Aspirina administrada a tiempo: la presión se mantiene estable.',
      },
      {
        id: 'iam-spo2-down',
        atMinutes: 6,
        nodeId: 'vital-spo2',
        newValue: 86,
        narrative: 'SpO2 baja a 86%. Edema pulmonar en progreso, se requiere O2 de alto flujo.',
        severity: 'severe',
        preventedByExamIds: ['exam-troponin', 'tx-aspirin'],
        preventedNarrative: 'El reconocimiento temprano del IAM permitió estabilizar la oxigenación.',
      },
      {
        id: 'iam-fr-up',
        atMinutes: 8,
        nodeId: 'vital-fr',
        newValue: 32,
        narrative: 'Taquipnea franca (FR 32). El paciente se deteriora rápidamente.',
        severity: 'severe',
        preventedByExamIds: ['tx-heparin'],
        preventedNarrative: 'La anticoagulación iniciada previene mayor compromiso hemodinámico.',
      },
      {
        id: 'iam-arrhythmia',
        atMinutes: 11,
        nodeId: 'vital-fc',
        newValue: 180,
        narrative: '⚠️ Arritmia ventricular. Requiere desfibrilación inmediata. CÓDIGO AZUL.',
        severity: 'life-threatening',
        preventedByExamIds: ['tx-heparin', 'tx-aspirin'],
        preventedNarrative: 'El manejo completo (AAS + heparina) reduce la probabilidad de arritmia maligna.',
      },
    ],
  },
};
